import React, { useState, useEffect } from 'react';
import { Product } from '../App';

export default function ProductDetail({ product, onBack, onAddToCart, onBuyWithPi, t, axiosClient }: { product: Product; onBack: () => void; onAddToCart: (p: Product) => void; onBuyWithPi: (p: Product) => void; t: (key: string) => string; axiosClient: any }) {
  const [isFav, setIsFav] = useState(false);
  const [reviews, setReviews] = useState<any[]>([]);
  const [reviewAvg, setReviewAvg] = useState('0.0');
  const [reviewTotal, setReviewTotal] = useState(0);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [rating, setRating] = useState(5);
  const [reviewContent, setReviewContent] = useState('');
  const [selectedSku, setSelectedSku] = useState<any>(null);

  // 检查是否已收藏
  useEffect(() => {
    const checkFav = async () => {
      try {
        const res = await axiosClient.get('/favorites');
        const favorites = res.data.favorites || [];
        setIsFav(favorites.some((f: any) => f.product && f.product.id === product.id));
      } catch (e) {}
    };
    checkFav();
  }, [product.id]);

  // 加载评价
  useEffect(() => {
    const loadReviews = async () => {
      try {
        const res = await axiosClient.get(`/reviews?productId=${product.id}`);
        setReviews(res.data.reviews || []);
        setReviewAvg(res.data.average || '0.0');
        setReviewTotal(res.data.total || 0);
      } catch (e) {}
    };
    loadReviews();
  }, [product.id]);

  const toggleFav = async () => {
    try {
      if (isFav) {
        await axiosClient.delete(`/favorites/${product.id}`);
        setIsFav(false);
      } else {
        await axiosClient.post('/favorites', { productId: product.id });
        setIsFav(true);
      }
    } catch (e) {}
  };

  const submitReview = async () => {
    try {
      await axiosClient.post('/reviews', {
        productId: product.id,
        rating,
        content: reviewContent,
      });
      setShowReviewForm(false);
      setReviewContent('');
      setRating(5);
      // 重新加载评价
      const res = await axiosClient.get(`/reviews?productId=${product.id}`);
      setReviews(res.data.reviews || []);
      setReviewAvg(res.data.average || '0.0');
      setReviewTotal(res.data.total || 0);
    } catch (e: any) {
      const msg = e.response?.data?.error;
      if (msg === 'Already reviewed') {
        alert(t('review.exists'));
      }
    }
  };

  return (
    <div>
      <div className="detail-header">
        <span className="detail-back" onClick={onBack}>←</span>
        <span className="detail-title">{t('detail.title')}</span>
        <span style={{width:36, cursor:'pointer', fontSize:'20px'}} onClick={toggleFav}>{isFav ? '❤️' : '🤍'}</span>
      </div>

      <img className="detail-image" src={product.image} alt={product.name} onError={(e: any) => e.target.src='data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 360"><rect fill="%23f0f0f0" width="400" height="360"/><text x="200" y="185" text-anchor="middle" fill="%23ccc" font-size="16">No Image</text></svg>'} />

      <div className="detail-info">
        {/* 价格行 - 根据 selectedSku 动态显示 */}
        <div className="detail-price-row">
          <span className="detail-pi-price">
            π{selectedSku ? selectedSku.piPrice : product.piPrice}
          </span>
          <span className="detail-original-price">
            ¥{selectedSku ? selectedSku.originalPrice : product.originalPrice}
          </span>
        </div>

        <div className="detail-name">{product.name}</div>

        {/* ✅ SKU 选择器 - 插入在这里（名称下方，销量上方） */}
        {product.skus && product.skus.length > 0 && (
          <div style={{marginTop:'12px'}}>
            <div style={{fontSize:'13px', fontWeight:600, color:'#333', marginBottom:'8px'}}>
              选择规格
            </div>
            <div style={{display:'flex', flexWrap:'wrap', gap:'8px'}}>
              {product.skus.map((sku: any) => (
                <button
                  key={sku.id}
                  onClick={() => setSelectedSku(sku)}
                  style={{
                    padding:'8px 16px',
                    border:'1px solid',
                    borderColor: selectedSku?.id === sku.id ? '#611f69' : '#ddd',
                    background: selectedSku?.id === sku.id ? '#f3e5f5' : '#fff',
                    color: selectedSku?.id === sku.id ? '#611f69' : '#333',
                    borderRadius:'6px',
                    cursor:'pointer',
                    fontSize:'13px'
                  }}
                >
                  <div>{sku.spec_name}</div>
                  <div style={{fontSize:'11px', color:'#888'}}>π{sku.piPrice}</div>
                </button>
              ))}
            </div>
            {selectedSku && (
              <div style={{marginTop:'8px', fontSize:'12px', color:'#666'}}>
                库存：{selectedSku.stock} 件
              </div>
            )}
          </div>
        )}

        {product.sales && <div className="detail-sales">{product.sales} {t('detail.sold')}</div>}
        {product.coupon && (
          <div style={{marginTop:8}}>
            <span className="product-card-coupon">{product.coupon}</span>
          </div>
        )}
        
        {/* 商品描述 */}
        {product.description && (
          <div style={{marginTop:'12px', padding:'12px', background:'#f8f9fa', borderRadius:'8px'}}>
            <div style={{fontSize:'13px', fontWeight:600, color:'#333', marginBottom:'6px'}}>
              {t('detail.description') || '商品详情'}
            </div>
            <div style={{fontSize:'13px', color:'#666', lineHeight:'1.6', whiteSpace:'pre-wrap'}}>
              {product.description}
            </div>
          </div>
        )}
      </div>

      {/* 评价区域 */}
      <div className="detail-desc">
        <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'12px'}}>
          <h3 style={{margin:0}}>{t('review.title')} ({reviewTotal})</h3>
          <div style={{display:'flex', alignItems:'center', gap:'8px'}}>
            <span style={{color:'#FFD700', fontSize:'18px'}}>⭐ {reviewAvg}</span>
            <button 
              onClick={() => setShowReviewForm(!showReviewForm)}
              style={{padding:'4px 12px', borderRadius:'16px', border:'1px solid #611f69', color:'#611f69', background:'white', fontSize:'12px', cursor:'pointer'}}
            >{t('review.write')}</button>
          </div>
        </div>

        {/* 评价表单 */}
        {showReviewForm && (
          <div style={{background:'#f9f9f9', borderRadius:'8px', padding:'12px', marginBottom:'12px'}}>
            <div style={{marginBottom:'8px'}}>
              <span style={{fontSize:'13px', color:'#666'}}>{t('review.rating')}: </span>
              {[1,2,3,4,5].map(i => (
                <span key={i} style={{cursor:'pointer', fontSize:'20px'}} onClick={() => setRating(i)}>
                  {i <= rating ? '⭐' : '☆'}
                </span>
              ))}
            </div>
            <textarea 
              value={reviewContent}
              onChange={(e) => setReviewContent(e.target.value)}
              placeholder={t('review.content')}
              style={{width:'100%', height:'60px', border:'1px solid #ddd', borderRadius:'8px', padding:'8px', fontSize:'13px', resize:'none', boxSizing:'border-box'}}
            />
            <button 
              onClick={submitReview}
              style={{marginTop:'8px', padding:'6px 20px', borderRadius:'16px', border:'none', background:'#611f69', color:'white', fontSize:'13px', cursor:'pointer'}}
            >{t('review.submit')}</button>
          </div>
        )}

        {/* 评价列表 */}
        {reviews.length === 0 ? (
          <p style={{color:'#999', fontSize:'13px', textAlign:'center'}}>{t('review.total')} 0</p>
        ) : (
          reviews.slice(0, 5).map((r: any, idx: number) => (
            <div key={idx} style={{borderBottom:'1px solid #f0f0f0', padding:'8px 0'}}>
              <div style={{display:'flex', justifyContent:'space-between', alignItems:'center'}}>
                <span style={{fontSize:'13px', fontWeight:500}}>{r.username || 'User'}</span>
                <span style={{color:'#FFD700', fontSize:'12px'}}>{'⭐'.repeat(r.rating)}</span>
              </div>
              {r.content && <p style={{fontSize:'12px', color:'#666', margin:'4px 0 0'}}>{r.content}</p>}
            </div>
          ))
        )}
      </div>

      <div style={{height:80}}></div>

      <div className="detail-bottom">
        <button className="btn-cart" onClick={() => onAddToCart(product)}>{t('detail.addCart')}</button>
        <button className="btn-buy" onClick={() => onBuyWithPi(product)}>{t('detail.buyNow')}</button>
      </div>
    </div>
  );
}
